//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';

import { SamplingCatalogProductsResponse } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import {
  searchBvCatalogProducts,
  searchCuralateCatalogProducts,
} from '@apis/universal-image-actions/universalImageActionsApi';
import { CuralateCatalogProduct } from '@apis/universal-image-actions/universalImageActionsApiModel';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { isDefined } from '@utils/filterPredicates';

const mapBVResponseToCuralateProducts = ({
  products,
}: SamplingCatalogProductsResponse): CuralateCatalogProduct[] => {
  if (isDefined(products)) {
    return products.reduce<CuralateCatalogProduct[]>(
      (formattedProducts, product) => {
        if (isDefined(product.styleId) && !!product.productDetailPageUrl) {
          formattedProducts.push({
            destinationUrl: product.productDetailPageUrl,
            images: [
              {
                fileId: product.fileId,
                imageUrl: product.imageUrl,
                sourceImageUrl: product.imageUrl,
              },
            ],
            name: product.name,
            productStyleId: product.styleId,
            styleId: product.styleId,
            description: product.description,
            externalId: product.samplingProductId,
          });
        }

        return formattedProducts;
      },
      [],
    );
  }
  return [];
};

export interface ProductsParams {
  hasCatalog: boolean;
  isBvCampaignTagged?: boolean;
  keywords: string;
  locale?: string;
}

export const useProductsQuery = ({
  hasCatalog,
  isBvCampaignTagged = false,
  keywords,
  locale,
}: ProductsParams) =>
  useQuery({
    enabled: hasCatalog,
    queryKey: [
      QueryKeyConstants.UniversalImageActions,
      QueryKeyConstants.UniversalImageActionsProducts,
      QueryKeyConstants.UniversalImageActionsProductsList,
      { isBvCampaignTagged, keywords, locale },
    ],
    queryFn: async ({ signal }) =>
      isBvCampaignTagged
        ? mapBVResponseToCuralateProducts(
            await searchBvCatalogProducts({ keywords }, { signal }),
          )
        : await searchCuralateCatalogProducts({ keywords, locale }, { signal }),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
